import { APP_FEATURE, useAppFeatures } from '@/lib/app-features';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LayoutGridIcon, PencilIcon, RefreshCwIcon, Trash2Icon } from 'lucide-react';
import { cn, cnSpin } from '@/lib/utils';
import {
  BackLink,
  Button,
  ConfirmDialog,
  Drawer,
  EmptyState,
  ErrorNotice,
  FactList,
  Field,
  LoadingState,
  MultiCombobox,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SelectInput,
  Tabs,
  TextArea,
  TextInput,
} from '@bcecloud/portal-ui';
import DOMPurify from 'dompurify';
import { errorMessage } from '@/lib/api';
import { LIST_QUERY_KEYS, useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';
import { ProgramKitPanel } from '@/pages/program-kit';
import { SEASON_TAGS_QUERY_KEY, listSeasonTags, type SeasonTag } from '@/lib/season-tags';
import {
  deleteProgram,
  listProgramCatalog,
  PROGRAM_CATALOG,
  readMyIdentity,
  readProgram,
  syncProgram,
  updateProgram,
  type Program,
  type ProgramCatalog,
  type ProgramPatch,
} from '@/lib/events';

/** The tab key in `?catalog=`. */
const CATALOG_PARAM = 'catalog';

/** Cards per page: divides evenly into the two- and three-column grids. */
const PAGE_SIZE = 60;

const CATALOG_TABS: readonly { readonly key: ProgramCatalog; readonly label: string }[] = [
  { key: PROGRAM_CATALOG.website, label: 'On the website' },
  { key: PROGRAM_CATALOG.all, label: 'All' },
];

/** Reads the tab from the URL; anything unknown opens the website tab. */
function catalogFromParam(value: string | null): ProgramCatalog {
  return value === PROGRAM_CATALOG.all ? PROGRAM_CATALOG.all : PROGRAM_CATALOG.website;
}

/**
 * The program catalog, as cards.
 *
 * Opens on what the website sells today (active in Salesforce and published
 * on the site); every program Salesforce has ever held, retired ones
 * included, is one tab away under All. Tab, search and page live in the URL.
 *
 * Read-only by design: programs sync from Salesforce and editing them needs a
 * V3 admin role the proxy's service account deliberately does not hold. What
 * staff need daily is to *know* the catalog — what a program is, who it
 * fits — and that is what this optimises for.
 *
 * @returns The programs page.
 */
export function ProgramsPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const catalog = catalogFromParam(params.get(CATALOG_PARAM));
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();

  const programs = useQuery({
    queryKey: ['programs', catalog, debounced, page],
    queryFn: ({ signal }) =>
      listProgramCatalog({ search: debounced, catalog, page, limit: PAGE_SIZE }, signal),
    placeholderData: keepPreviousData,
  });

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="CATALOG"
        title="Programs"
        subtitle="Every program BCE delivers. Content syncs from Salesforce."
      />

      <div className="mb-5">
        <Tabs<ProgramCatalog>
          label="Program catalog"
          items={CATALOG_TABS}
          active={catalog}
          onChange={(next) =>
            setParams(
              (previous) => {
                const nextParams = new URLSearchParams(previous);
                if (next === PROGRAM_CATALOG.website) nextParams.delete(CATALOG_PARAM);
                else nextParams.set(CATALOG_PARAM, next);
                nextParams.delete(LIST_QUERY_KEYS.page);
                return nextParams;
              },
              { replace: true },
            )
          }
        />
      </div>

      <div className="mb-4">
        <SearchInput
          label="Search programs"
          placeholder="Program name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
      </div>

      {programs.isError ? (
        <ErrorNotice title="Programs unavailable" message={errorMessage(programs.error)} />
      ) : programs.isPending ? (
        <LoadingState label="Loading programs…" />
      ) : programs.data.programs.length === 0 ? (
        <Panel>
          {debounced ? (
            <EmptyState message="No programs match that search." />
          ) : (
            <EmptyState
              icon={<LayoutGridIcon aria-hidden className="size-6" />}
              title="No programs yet"
              message={
                catalog === PROGRAM_CATALOG.website
                  ? 'Programs that are active in Salesforce and published on the website appear here.'
                  : 'The program catalog syncs from Salesforce and appears here.'
              }
            />
          )}
        </Panel>
      ) : (
        <>
        <div
          className={cn(
            'grid gap-4 sm:grid-cols-2 xl:grid-cols-3',
            programs.isFetching && 'opacity-60 transition-opacity',
          )}
        >
          {programs.data.programs.map((program) => (
            <Link key={program._id} to={`/programs/${program._id}`} className="group">
              <Panel className="h-full overflow-hidden transition-shadow group-hover:shadow-portal-raised">
                {program.images?.[0] ? (
                  <img
                    src={program.images[0]}
                    alt=""
                    className="h-[140px] w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="grid h-[140px] w-full place-items-center bg-portal-sky text-brand/40">
                    <LayoutGridIcon aria-hidden className="size-9" />
                  </div>
                )}
                <div className="p-4">
                  <h2 className="text-portal-card-title font-bold group-hover:text-brand">
                    {program.displayName ?? program.name}
                  </h2>
                  {program.excerpt ? (
                    <p className="mt-1 line-clamp-2 text-portal-small text-portal-muted">
                      {program.excerpt}
                    </p>
                  ) : null}
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {/* Only the All tab mixes in programs the website no longer sells. */}
                    {catalog === PROGRAM_CATALOG.all && !isOnWebsite(program) ? (
                      <Pill tone="neutral">{program.isActive === false ? 'Inactive' : 'Not on website'}</Pill>
                    ) : null}
                    {[program.groupSize, program.length, program.setting]
                      .filter((value): value is string => Boolean(value))
                      .map((value) => (
                        <Pill key={value} tone="neutral">
                          {value}
                        </Pill>
                      ))}
                  </div>
                </div>
              </Panel>
            </Link>
          ))}
        </div>
        <Pagination
          page={programs.data.page}
          totalPages={programs.data.totalPages}
          onChange={setPage}
          summary={`${(programs.data.page - 1) * PAGE_SIZE + 1}–${
            (programs.data.page - 1) * PAGE_SIZE + programs.data.programs.length
          } of ${programs.data.total.toLocaleString()}`}
          className="mt-4"
        />
        </>
      )}
    </section>
  );
}

/** Whether a program is on the website tab's slice (mirrors the API filter). */
function isOnWebsite(program: Program): boolean {
  return program.isActive !== false && program.onWebsite === true;
}

/**
 * One program, in full.
 *
 * @returns The program detail page.
 */
export function ProgramDetailPage(): React.ReactElement {
  const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const isFeatureOn = useAppFeatures();
  // Contractors reach this page for a program's CONTENT (the assistant's
  // program card, an event's program link). The catalog list and every
  // mutation stay internal — hide the controls the API would refuse.
  const identity = useQuery({
    queryKey: ['my-identity'],
    queryFn: ({ signal }) => readMyIdentity(signal),
    staleTime: 5 * 60_000,
  });
  const isInternal = ['internal', 'admin', 'superadmin'].includes(identity.data?.role ?? '');

  // Contractors cannot read the tag list, and have no use for it.
  const seasonTags = useQuery({
    queryKey: SEASON_TAGS_QUERY_KEY,
    queryFn: ({ signal }) => listSeasonTags(signal),
    enabled: isInternal,
  });

  const program = useQuery({
    queryKey: ['program', programId],
    queryFn: ({ signal }) => readProgram(programId ?? '', signal),
    enabled: programId !== undefined,
  });

  const sync = useMutation({
    mutationFn: () => syncProgram(program.data?.salesforceId ?? ''),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['program', programId] }),
  });
  const remove = useMutation({
    mutationFn: () => deleteProgram(programId ?? ''),
    onSuccess: () => navigate('/programs'),
  });

  if (program.isPending) return <LoadingState label="Loading program…" />;
  if (program.isError) {
    return <ErrorNotice title="Program unavailable" message={errorMessage(program.error)} />;
  }

  const detail = program.data;

  return (
    <section className="animate-portal-rise">
      {/* The catalog list is staff-only — no dead back-link for contractors. */}
      {isInternal ? <BackLink to="/programs">All programs</BackLink> : null}

      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <PageHeading
          eyebrow="PROGRAM"
          title={detail.displayName ?? detail.name ?? 'Program'}
          subtitle={detail.excerpt ? stripHtml(detail.excerpt) : undefined}
          action={detail.isActive === false ? <Pill tone="neutral">Inactive</Pill> : undefined}
        />
        {isInternal ? (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => sync.mutate()}
              disabled={sync.isPending || !detail.salesforceId}
            >
              <RefreshCwIcon aria-hidden className={cnSpin(sync.isPending)} />
              {sync.isPending ? 'Syncing…' : 'Sync'}
            </Button>
            <Button guide="program-edit" variant="secondary" onClick={() => setEditing(true)}>
              <PencilIcon aria-hidden className="size-4" /> Edit
            </Button>
            <Button
              variant="secondary"
              className="text-red-600 hover:bg-red-50"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2Icon aria-hidden className="size-4" /> Delete
            </Button>
          </div>
        ) : null}
      </div>

      {sync.isError ? (
        <div className="mt-3">
          <ErrorNotice inline title="Sync failed" message={errorMessage(sync.error)} />
        </div>
      ) : null}

      <Panel guide="program-facts" className="mt-4 p-5">
        <FactList
          columns={3}
          showEmpty
          facts={[
            {
              label: 'Website ID',
              value: detail.websiteId ? (
                <ExternalId
                  href={`https://bestcorporateevents.com/wp-admin/post.php?post=${detail.websiteId}&action=edit`}
                >
                  {detail.websiteId}
                </ExternalId>
              ) : undefined,
            },
            {
              label: 'Salesforce Product ID',
              value: detail.salesforceId ? (
                <ExternalId href={`https://bestcorporateevents.lightning.force.com/${detail.salesforceId}`}>
                  {detail.salesforceId}
                </ExternalId>
              ) : undefined,
            },
            {
              label: 'Salesforce Program ID',
              value: detail.salesforceProgramId ? (
                <ExternalId
                  href={`https://bestcorporateevents.lightning.force.com/${detail.salesforceProgramId}`}
                >
                  {detail.salesforceProgramId}
                </ExternalId>
              ) : undefined,
            },
            { label: 'Is active', value: detail.isActive === undefined ? undefined : detail.isActive ? 'Yes' : 'No' },
            { label: 'Tags', value: detail.tags?.length ? detail.tags.join(', ') : undefined },
            ...(isInternal ? [{ label: 'Seasons', value: seasonsLabel(detail.seasonTagIds, seasonTags.data) }] : []),
            { label: 'Group size', value: detail.groupSize },
            { label: 'Team size', value: detail.teamSize },
            { label: 'Physicality', value: detail.physicality },
            { label: 'Program length', value: detail.length },
            { label: 'Space requirement', value: detail.spaceRequirement },
            { label: 'Setting', value: detail.setting },
            { label: 'Pricing includes', value: detail.pricingIncludes },
            { label: 'Pricing type', value: detail.pricingType },
            { label: 'Created', value: formatDateTime(detail.createdAt) },
            { label: 'Updated', value: formatDateTime(detail.updatedAt) },
          ]}
        />
      </Panel>

      {isFeatureOn(APP_FEATURE.materials) ? (
        <ProgramKitPanel key={JSON.stringify(detail.kit ?? [])} programId={detail._id} kit={detail.kit ?? []} canEdit={isInternal} />
      ) : null}

      <ProgramContent detail={detail} />

      <EditProgramDrawer
        program={detail}
        seasonTags={seasonTags.data ?? []}
        open={editing}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          void queryClient.invalidateQueries({ queryKey: ['program', programId] });
        }}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this program?"
        message={`"${detail.displayName ?? detail.name ?? 'This program'}" will be removed. This cannot be undone.`}
        confirmLabel="Delete"
        tone="danger"
        pending={remove.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
      />
    </section>
  );
}

/**
 * The program's season tags for the facts panel: "National Volunteer Month
 * (April), Summer outings (June to August)". A retired tag says so.
 *
 * @param ids - The program's tag ids.
 * @param tags - Every tag, when loaded.
 * @returns The label, or undefined when there are none.
 */
function seasonsLabel(ids: readonly string[] | undefined, tags: readonly SeasonTag[] | undefined): string | undefined {
  if (!ids || ids.length === 0 || !tags) return undefined;
  const names = ids.flatMap((id) => {
    const tag = tags.find((candidate) => candidate._id === id);
    if (!tag) return [];
    return [`${tag.name} (${tag.monthsLabel}${tag.isActive ? '' : ', retired'})`];
  });
  return names.length > 0 ? names.join(', ') : undefined;
}

/** Strips HTML tags to plain text, for a subtitle. */
function stripHtml(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });
}

/** Formats an ISO timestamp like "Aug 6, 2026, 11:02 PM", or undefined. */
function formatDateTime(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? undefined : date.toLocaleString();
}

/** A monospace external-id link (Salesforce / website admin). */
function ExternalId({ href, children }: { href: string; children: React.ReactNode }): React.ReactElement {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="font-mono text-portal-small text-brand hover:underline"
    >
      {children}
    </a>
  );
}

/**
 * The program's marketing content — descriptions, videos, images.
 *
 * Absent pieces don't render as their own empty cards: a program with no
 * content at all gets ONE promoted empty state, and a partially-filled one
 * shows only what exists plus a single quiet "not added yet" line. A wall of
 * "No X yet." boxes tells the reader seven times what one line can.
 *
 * @param props - The program.
 * @returns The content sections, or the first-run empty state.
 */
function ProgramContent({ detail }: { readonly detail: Program }): React.ReactElement {
  const description = detail.htmlContent || detail.description;
  const unbranded = detail.unbrandedHtmlContent || detail.unbrandedDescription;
  const safeImages = (detail.images ?? [])
    .map((image) => safeHttpUrl(image))
    .filter((image): image is string => image !== undefined);

  const missing: string[] = [];
  if (!detail.excerpt?.trim()) missing.push('short description');
  if (!description?.trim()) missing.push('description');
  if (!detail.proposalDescription?.trim()) missing.push('proposal description');
  if (!unbranded?.trim()) missing.push('unbranded description');
  if (!detail.video) missing.push('branded video');
  if (!detail.unbrandedVideo) missing.push('unbranded video');
  if (safeImages.length === 0) missing.push('images');

  const CONTENT_PIECES = 7;
  if (missing.length === CONTENT_PIECES) {
    return (
      <Panel className="mt-6">
        <EmptyState
          title="No content yet"
          message="Descriptions, videos and images sync from the website and Salesforce — or add them here with Edit program."
        />
      </Panel>
    );
  }

  return (
    <div className="mt-6 space-y-6">
      <DescriptionBlock title="Short description" html={detail.excerpt} />
      {/* The website's rendered HTML keeps the headings and lists the
          tag-stripped description field flattens away. */}
      <DescriptionBlock title="Description" html={description} />
      <DescriptionBlock title="Proposal description" html={detail.proposalDescription} />
      <DescriptionBlock title="Unbranded description" html={unbranded} />

      {detail.video || detail.unbrandedVideo ? (
        <div>
          <h2 className="mb-3 text-portal-section font-bold">Videos</h2>
          <div className="grid gap-5 md:grid-cols-2">
            <ProgramVideo label="Branded video" url={detail.video} />
            <ProgramVideo label="Unbranded video" url={detail.unbrandedVideo} />
          </div>
        </div>
      ) : null}

      {safeImages.length > 0 ? (
        <div>
          <h2 className="mb-3 text-portal-section font-bold">Images</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {safeImages.map((image, index) => (
              <a
                key={index}
                href={image}
                target="_blank"
                rel="noreferrer noopener"
                className="block overflow-hidden rounded-portal-card border border-portal-line"
              >
                <img
                  src={image}
                  alt={`${detail.name ?? 'Program'} ${index + 1}`}
                  className="aspect-[4/3] w-full object-cover transition-transform hover:scale-105"
                />
              </a>
            ))}
          </div>
        </div>
      ) : null}

      {missing.length > 0 ? (
        <p className="text-portal-micro text-portal-muted">Not added yet: {missing.join(', ')}.</p>
      ) : null}
    </div>
  );
}

/** A titled rich-text description block; renders nothing when absent. */
function DescriptionBlock({
  title,
  html,
}: {
  title: string;
  html: string | undefined;
}): React.ReactElement | null {
  if (!html || !html.trim()) return null;
  return (
    <div>
      <h2 className="mb-3 text-portal-section font-bold">{title}</h2>
      <Panel className="p-5">
        <div
          // No typography plugin in this app, so the website markup gets
          // explicit rules: spaced paragraphs, bold section headings,
          // bulleted lists — mirroring how the site itself renders it.
          className={cn(
            'max-w-none text-portal-body',
            '[&_p]:mb-3 [&_p]:leading-relaxed',
            '[&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:text-portal-card-title [&_h2]:font-bold',
            '[&_h3]:mt-5 [&_h3]:mb-2 [&_h3]:text-portal-card-title [&_h3]:font-bold',
            '[&_h4]:mt-4 [&_h4]:mb-1.5 [&_h4]:font-bold',
            '[&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5',
            '[&_li]:mb-1 marker:[&_li]:text-portal-muted',
            '[&_a]:text-brand-link [&_a]:underline',
          )}
          // Rich text synced from the website/Salesforce; sanitized before render.
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
        />
      </Panel>
    </div>
  );
}

/**
 * Returns the URL only if it is an http(s) URL — otherwise undefined.
 *
 * Program media URLs are staff-editable and synced from external systems, so a
 * `javascript:` (or other) scheme could slip in; guarding here stops it ever
 * reaching an href/src.
 *
 * @param url - A candidate URL.
 * @returns The URL when it uses http(s), else undefined.
 */
function safeHttpUrl(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.toString() : undefined;
  } catch {
    return undefined;
  }
}

/** Converts a video URL to an embeddable iframe src (YouTube/Vimeo), else null. */
function videoEmbedUrl(url: string): string | null {
  const yt = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/i.exec(url);
  if (yt?.[1]) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = /vimeo\.com\/(?:video\/)?(\d+)/i.exec(url);
  if (vimeo?.[1]) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

/** One labelled program video: its URL plus an embedded player. */
function ProgramVideo({ label, url }: { label: string; url: string | undefined }): React.ReactElement | null {
  if (!url) return null;
  const safe = safeHttpUrl(url);
  const embed = safe ? videoEmbedUrl(safe) : null;
  return (
    <div>
      <p className="mb-2 text-portal-small font-bold">
        {label}:{' '}
        {safe ? (
          <a href={safe} target="_blank" rel="noreferrer noopener" className="font-normal text-brand hover:underline">
            {url}
          </a>
        ) : (
          <span className="font-normal break-all text-portal-muted">{url}</span>
        )}
      </p>
      <div className="aspect-video w-full overflow-hidden rounded-portal-card border border-portal-line bg-black">
        {embed ? (
          <iframe src={embed} title={label} className="h-full w-full" allowFullScreen />
        ) : safe ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video src={safe} controls className="h-full w-full" />
        ) : (
          <div className="grid h-full place-items-center text-portal-small text-white/70">
            Unsupported video URL
          </div>
        )}
      </div>
    </div>
  );
}

/** The fields the edit drawer manages, as editable strings. */
interface EditForm {
  name: string;
  isActive: string;
  tags: string;
  seasonTagIds: readonly string[];
  groupSize: string;
  teamSize: string;
  physicality: string;
  length: string;
  spaceRequirement: string;
  setting: string;
  pricingIncludes: string;
  pricingType: string;
  video: string;
  unbrandedVideo: string;
  excerpt: string;
  description: string;
  proposalDescription: string;
  unbrandedDescription: string;
}

/** Builds the initial edit form from a program. */
function toForm(program: Program): EditForm {
  return {
    name: program.name ?? '',
    isActive: program.isActive === false ? 'no' : 'yes',
    tags: program.tags?.join(', ') ?? '',
    seasonTagIds: program.seasonTagIds ?? [],
    groupSize: program.groupSize ?? '',
    teamSize: program.teamSize ?? '',
    physicality: program.physicality ?? '',
    length: program.length ?? '',
    spaceRequirement: program.spaceRequirement ?? '',
    setting: program.setting ?? '',
    pricingIncludes: program.pricingIncludes ?? '',
    pricingType: program.pricingType ?? '',
    video: program.video ?? '',
    unbrandedVideo: program.unbrandedVideo ?? '',
    excerpt: program.excerpt ?? '',
    description: program.description ?? '',
    proposalDescription: program.proposalDescription ?? '',
    unbrandedDescription: program.unbrandedDescription ?? '',
  };
}

/**
 * A side-panel form to edit every program field, saved via PUT /programs/:id.
 *
 * @param props - The program, open state, and close/saved handlers.
 * @returns The edit drawer.
 */
function EditProgramDrawer({
  program,
  seasonTags,
  open,
  onClose,
  onSaved,
}: {
  program: Program;
  /** Every season tag; retired ones are offered only while the program still carries them. */
  seasonTags: readonly SeasonTag[];
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}): React.ReactElement {
  const [form, setForm] = useState<EditForm>(() => toForm(program));

  // Re-seed from the record whenever the drawer opens.
  useEffect(() => {
    if (open) setForm(toForm(program));
  }, [open, program]);

  const save = useMutation({
    mutationFn: () => {
      const patch: ProgramPatch = {
        name: form.name.trim(),
        isActive: form.isActive === 'yes',
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        seasonTagIds: form.seasonTagIds,
        groupSize: form.groupSize.trim(),
        teamSize: form.teamSize.trim(),
        physicality: form.physicality.trim(),
        length: form.length.trim(),
        spaceRequirement: form.spaceRequirement.trim(),
        setting: form.setting.trim(),
        pricingIncludes: form.pricingIncludes.trim(),
        pricingType: form.pricingType.trim(),
        video: form.video.trim(),
        unbrandedVideo: form.unbrandedVideo.trim(),
        excerpt: form.excerpt,
        description: form.description,
        proposalDescription: form.proposalDescription,
        unbrandedDescription: form.unbrandedDescription,
      };
      return updateProgram(program._id, patch);
    },
    onSuccess: onSaved,
  });

  const set = <K extends keyof EditForm>(key: K, value: EditForm[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Drawer open={open} onClose={onClose} title="Edit program">
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Name" htmlFor="p-name">
          <TextInput id="p-name" value={form.name} onChange={(e) => set('name', e.target.value)} />
        </Field>

        <Field label="Active" htmlFor="p-active">
          <SelectInput id="p-active" value={form.isActive} onChange={(e) => set('isActive', e.target.value)}>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </SelectInput>
        </Field>

        <Field
          guide="program-seasons"
          label="Seasons"
          htmlFor="p-seasons"
          hint="Clients see this program as an idea on their calendar in these seasons. Manage the list under Catalog → Season tags."
        >
          <MultiCombobox
            id="p-seasons"
            options={seasonTags
              .filter((tag) => tag.isActive || form.seasonTagIds.includes(tag._id))
              .map((tag) => ({ value: tag._id, label: `${tag.name} (${tag.monthsLabel})` }))}
            values={form.seasonTagIds}
            placeholder="Pick seasons"
            onChange={(values) => set('seasonTagIds', values)}
          />
        </Field>

        <Field label="Tags (comma-separated)" htmlFor="p-tags">
          <TextInput id="p-tags" value={form.tags} onChange={(e) => set('tags', e.target.value)} />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Group size" htmlFor="p-group">
            <TextInput id="p-group" value={form.groupSize} onChange={(e) => set('groupSize', e.target.value)} />
          </Field>
          <Field label="Team size" htmlFor="p-team">
            <TextInput id="p-team" value={form.teamSize} onChange={(e) => set('teamSize', e.target.value)} />
          </Field>
          <Field label="Physicality" htmlFor="p-phys">
            <TextInput id="p-phys" value={form.physicality} onChange={(e) => set('physicality', e.target.value)} />
          </Field>
          <Field label="Length" htmlFor="p-len">
            <TextInput id="p-len" value={form.length} onChange={(e) => set('length', e.target.value)} />
          </Field>
          <Field label="Space requirement" htmlFor="p-space">
            <TextInput id="p-space" value={form.spaceRequirement} onChange={(e) => set('spaceRequirement', e.target.value)} />
          </Field>
          <Field label="Setting" htmlFor="p-setting">
            <TextInput id="p-setting" value={form.setting} onChange={(e) => set('setting', e.target.value)} />
          </Field>
          <Field label="Pricing type" htmlFor="p-ptype">
            <TextInput id="p-ptype" value={form.pricingType} onChange={(e) => set('pricingType', e.target.value)} />
          </Field>
        </div>

        <Field label="Pricing includes" htmlFor="p-pincl">
          <TextArea id="p-pincl" rows={2} value={form.pricingIncludes} onChange={(e) => set('pricingIncludes', e.target.value)} />
        </Field>
        <Field label="Branded video URL" htmlFor="p-video">
          <TextInput id="p-video" value={form.video} onChange={(e) => set('video', e.target.value)} placeholder="https://…" />
        </Field>
        <Field label="Unbranded video URL" htmlFor="p-uvideo">
          <TextInput id="p-uvideo" value={form.unbrandedVideo} onChange={(e) => set('unbrandedVideo', e.target.value)} placeholder="https://…" />
        </Field>

        <Field label="Short description (HTML)" htmlFor="p-excerpt">
          <TextArea id="p-excerpt" rows={3} value={form.excerpt} onChange={(e) => set('excerpt', e.target.value)} />
        </Field>
        <Field label="Description (HTML)" htmlFor="p-desc">
          <TextArea id="p-desc" rows={6} value={form.description} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <Field label="Proposal description (HTML)" htmlFor="p-prop">
          <TextArea id="p-prop" rows={4} value={form.proposalDescription} onChange={(e) => set('proposalDescription', e.target.value)} />
        </Field>
        <Field label="Unbranded description (HTML)" htmlFor="p-unbr">
          <TextArea id="p-unbr" rows={4} value={form.unbrandedDescription} onChange={(e) => set('unbrandedDescription', e.target.value)} />
        </Field>

        {save.error ? <ErrorNotice inline title="Could not save" message={errorMessage(save.error)} /> : null}

        <div className="flex items-center gap-3 pt-2">
          <Button guide="program-save" type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save program'}
          </Button>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
